import { pascalCase } from '@takeshape/util';
import { fromJS } from 'immutable';
import kebabCase from 'lodash/kebabCase';
import { createReducer } from 'typesafe-actions';
import type { ImmutableMap } from '../common/utils/immutable-types.js';
import { CHANGE_FIELD, REGISTER_FORM } from '../schema-form/action-types.js';
import type { ChangeFieldAction, RegisterFormAction } from '../schema-form/actions.js';
import type { FormState } from '../schema-form/types.js';
import { SERVICE_FORM_NAME } from './constants.js';
import providers from './providers/index.js';

type State = ImmutableMap<FormState>;

const shouldSetIdProviders = new Set(Object.keys(providers).filter((key) => providers[key].titleToId));
const shouldSetNamespaceProviders = new Set(Object.keys(providers).filter((key) => providers[key].titleToNamespace));

const setAudienceProviders = new Set(
  Object.keys(providers).filter((key) => providers[key].getFormConfig?.().properties?.audience)
);

const getAudienceValue = (projectId: string, slug?: string): string =>
  `https://api.takeshape.io/project/${projectId}/${slug ? slug : ''}`;

export function changeField(state: State, { meta, payload }: ChangeFieldAction): State {
  const isTitleField = meta.path[0] === 'title';
  const isNew = state.getIn<boolean>(['data', 'isNew']);
  const providerName = state.getIn<string>(['data', 'provider']);
  const projectId = state.getIn<string>(['data', 'projectId']);

  if (!providerName || !projectId || !isNew || !isTitleField) {
    return state;
  }

  const shouldSetId = shouldSetIdProviders.has(providerName);
  const shouldSetNamespace = shouldSetNamespaceProviders.has(providerName);
  const shouldSetAudience = setAudienceProviders.has(providerName);

  if (shouldSetId || shouldSetAudience) {
    const slug = kebabCase(payload as string);

    if (slug && shouldSetId) {
      state = state.setIn(['data', 'id'], slug);
    }

    if (shouldSetAudience) {
      state = state.setIn(['data', 'audience'], getAudienceValue(projectId, slug));
    }
  }

  if (shouldSetNamespace) {
    const namespace = pascalCase(payload as string);

    state = state.setIn(['data', 'namespace'], namespace);
  }

  return state;
}

export function registerForm(state: State): State {
  const title = state.getIn<string>(['data', 'title']);
  const audience = state.getIn<string>(['data', 'audience']);
  const providerName = state.getIn<string>(['data', 'provider']);
  const projectId = state.getIn<string>(['data', 'projectId']);

  if (!audience && providerName && projectId && setAudienceProviders.has(providerName)) {
    state = state.setIn(['data', 'audience'], getAudienceValue(projectId, kebabCase(title)));
  }

  return state;
}

export const servicesFormReducers = {
  [SERVICE_FORM_NAME]: createReducer<State, ChangeFieldAction | RegisterFormAction>(fromJS({}))
    .handleType(CHANGE_FIELD, changeField)
    .handleType(REGISTER_FORM, registerForm)
};
